import NProgress from "nprogress";
import * as qs from "qs";
import { createRouter, createWebHistory } from "vue-router";
import "nprogress/nprogress.css";

import {
  accessGuard,
  deactivatedGuard,
  deskGuard,
  feedDetailGuard,
  homeGuard,
  pendingApprovalGuard,
} from "@/router/navigationGuards.ts";
import type { Project } from "@/types/globalTypes";

declare module "vue-router" {
  interface RouteMeta {
    application: Project;
    access?: "public" | "authenticated" | "app";
    permission?: Project | Project[];
    layout:
      | "DefaultLayout"
      | "CvdLayout"
      | "ArchiveLayout"
      | "PlainLayout"
      | "SinglePageLayout"
      | "IntelligenceLayout";
  }
}

function parseQueryString(search: string) {
  return qs.parse(search, {
    parseArrays: true,
    comma: true,
    ignoreQueryPrefix: true,
  });
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  // @ts-ignore -- technical limitation of vue router here that causes an error
  parseQuery: parseQueryString,
  stringifyQuery(query) {
    return qs.stringify(query, {
      encode: false,
      addQueryPrefix: false,
    });
  },
  routes: [
    {
      path: "/",
      name: "root",
      component: () => import("@/views/NotFoundView.vue"),
      meta: {
        application: "archive",
        layout: "SinglePageLayout",
      },
      beforeEnter: [homeGuard],
    },
    {
      path: "/desk",
      name: "desk",
      props: true,
      component: () => import("@/views/desk/DesksView.vue"),
      meta: {
        application: "desk",
        layout: "DefaultLayout",
        permission: "desk",
      },
      beforeEnter: [deskGuard],
      children: [
        {
          path: ":id",
          name: "DeskDetail",
          component: () => import("@/views/desk/DesksDetailView.vue"),
        },
      ],
    },
    {
      path: "/archiv",
      children: [
        {
          path: "",
          name: "feed",
          component: () => import("@/views/archive/FeedView.vue"),
          meta: {
            application: "archive",
            layout: "ArchiveLayout",
            permission: "archive",
          },
        },
        {
          path: "feed/detail/:urn/:type",
          name: "feedDetail",
          props: true,
          meta: {
            application: "archive",
            layout: "ArchiveLayout",
            permission: "archive",
          },
          components: {
            default: () => import("@/views/archive/FeedView.vue"),
            dynamicContent: () => import("@/views/archive/FeedDetailPage.vue"),
          },
          beforeEnter: [feedDetailGuard],
        },
        {
          path: "detail/:urn/:type?",
          name: "detailPage",
          props: true,
          meta: {
            application: "archive",
            layout: "SinglePageLayout",
          },
          component: () => import("@/views/archive/DetailPage.vue"),
        },
      ],
    },
    {
      path: "/cvd",
      component: () => import("@/views/Cvd/HomeView.vue"),
      meta: {
        application: "cvd",
        layout: "CvdLayout",
        permission: "cvd",
      },
      redirect: { name: "cvdPage", params: { slug: ["top-themen"] } },
      children: [
        {
          path: ":slug+",
          children: [
            {
              path: "",
              name: "cvdPage",
              component: () => import("@/views/Cvd/PageView.vue"),
              meta: {
                application: "cvd",
                layout: "CvdLayout",
              },
            },
          ],
        },
        {
          path: "detail/:urn",
          props: true,
          name: "detail",
          component: () => import("@/views/Cvd/DetailView.vue"),
          meta: {
            application: "cvd",
            layout: "DefaultLayout",
          },
        },
        {
          path: "photogallery/:urn",
          name: "photogallery",
          props: true,
          component: () => import("@/views/Cvd/GalleryView.vue"),
          meta: {
            application: "cvd",
            layout: "SinglePageLayout",
          },
        },
      ],
    },
    {
      path: "/messenger",
      component: () => import("@/views/messenger/HomeView.vue"),
      redirect: { name: "messages" },
      name: "messenger",
      meta: {
        application: "messenger",
        layout: "PlainLayout",
        permission: "messenger",
      },
      children: [
        {
          path: "messages",
          name: "messages",
          component: () => import("@/views/messenger/MessengerView.vue"),
          children: [
            {
              path: ":id",
              name: "message",
              props: true,
              component: () => import("@/views/messenger/MessageView.vue"),
            },
          ],
        },
        {
          path: "comments",
          name: "comments",
          component: () => import("@/views/messenger/CommentsView.vue"),
          children: [
            {
              path: ":id",
              name: "comment",
              props: true,
              component: () => import("@/views/messenger/CommentView.vue"),
            },
          ],
        },
      ],
    },
    {
      path: "/ai-research",
      name: "ai-research",
      meta: {
        application: "ai-research",
        layout: "PlainLayout",
        permission: "ai-research",
      },
      component: () => import("../views/aiResearch/AiResearch.vue"),
    },
    {
      path: "/bookmark",
      name: "bookmark",
      component: () => import("@/views/archive/BookmarkView.vue"),
      meta: {
        application: "archive",
        layout: "DefaultLayout",
        permission: "archive",
      },
    },
    {
      path: "/privacy",
      name: "privacy",
      meta: {
        access: "public",
        application: "archive",
        layout: "SinglePageLayout",
      },
      component: () => import("@/views/PrivacyView.vue"),
    },
    {
      path: "/impressum",
      name: "impressum",
      meta: {
        access: "public",
        application: "archive",
        layout: "SinglePageLayout",
      },
      component: () => import("@/views/ImprintView.vue"),
    },
    {
      path: "/terms-of-use",
      name: "termsOfUse",
      meta: {
        access: "public",
        application: "archive",
        layout: "SinglePageLayout",
      },
      component: () => import("@/views/AgbView.vue"),
    },
    {
      path: "/cookie-policy",
      name: "CookiePolicy",
      meta: {
        access: "public",
        application: "archive",
        layout: "SinglePageLayout",
      },
      component: () => import("@/views/CookiePolicy.vue"),
    },
    {
      path: "/pending-approval",
      name: "pendingApproval",
      meta: {
        access: "authenticated",
        application: "archive",
        layout: "PlainLayout",
      },
      beforeEnter: [pendingApprovalGuard],
      component: () => import("@/views/SignUpPendingView.vue"),
    },
    {
      path: "/deactivated",
      name: "deactivated",
      meta: {
        access: "authenticated",
        application: "archive",
        layout: "PlainLayout",
      },
      beforeEnter: [deactivatedGuard],
      component: () => import("@/views/DeactivatedView.vue"),
    },
    {
      path: "/settings",
      name: "settingsPage",
      meta: {
        application: "archive",
        layout: "DefaultLayout",
        permission: ["archive", "intelligence"],
      },
      component: () => import("@/views/SettingsView.vue"),
    },
    {
      path: "/intelligence",
      name: "intelligence",
      redirect: { name: "intelligence-overview" },
      meta: {
        application: "intelligence",
        layout: "IntelligenceLayout",
        permission: "intelligence",
      },
      component: () => import("@/views/intelligence/IntelligenceView.vue"),
      children: [
        {
          path: "profile/overview",
          name: "intelligence-overview",
          component: () =>
            import("@/views/intelligence/overview/ProfileOverview.vue"),
        },
        {
          path: "profile/generator",
          name: "intelligence-generator",
          component: () =>
            import("@/views/intelligence/generator/ProfileGeneratorView.vue"),
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      name: "notFound",
      meta: {
        application: "archive",
        layout: "SinglePageLayout",
        access: "authenticated",
      },
      component: () => import("@/views/NotFoundView.vue"),
    },
    {
      path: "/forbidden",
      name: "forbidden",
      meta: {
        application: "archive",
        layout: "SinglePageLayout",
        access: "authenticated",
      },
      component: () => import("@/views/ForbiddenView.vue"),
    },
    {
      path: "/login-error",
      name: "loginError",
      meta: {
        access: "public",
        application: "archive",
        layout: "SinglePageLayout",
      },
      component: () => import("@/views/LoginErrorView.vue"),
    },
    {
      path: "/ai-faq",
      name: "ai-faq",
      meta: {
        access: "public",
        application: "ai-research",
        layout: "SinglePageLayout",
      },
      component: () => import("../views/aiResearch/AiResearchFaq.vue"),
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    // @ts-ignore
    if (
      (typeof to.name === "string" && to.name?.includes("detailPage")) ||
      to.name === "photogallery" ||
      to.name === "detail" ||
      to.name === "impressum" ||
      to.name === "privacy" ||
      to.name === "termsOfUse" ||
      to.name === "CookiePolicy"
    ) {
      return {
        top: 0,
      };
    }
    if (savedPosition) {
      return savedPosition;
    }
  },
});
/** optional configuration for progress indicator
 * NProgress.configure({ showSpinner: false, speed: 400, minimum: 0.1 });
 */
NProgress.configure({ showSpinner: false, speed: 500, minimum: 0.1 });
router.beforeEach((to) => {
  if (to.name) {
    NProgress.start();
  }
});
router.beforeEach(accessGuard);

router.afterEach(() => {
  NProgress.done();
  // ensures Appcues page tracking is always triggered after navigation.
  if (window.Appcues && typeof window.Appcues.page === "function") {
    window.Appcues.page();
  }
});

// this is to fix Sentry Error: DPA-NEWS-1YS

router.onError((error) => {
  // Detect chunk loading errors (dynamic import failures)
  const chunkFailedMessage =
    /Loading chunk [\d\w]+ failed|error loading dynamically imported module/i;
  if (chunkFailedMessage.test(error.message)) {
    // Prevent infinite reload loops
    if (!sessionStorage.getItem("chunkLoadError")) {
      sessionStorage.setItem("chunkLoadError", "true");
      // Optionally, show a user-friendly message here
      window.location.reload();
    }
  }
});
export default router;
